/**
 * Buscador de clave oficial LOCALIDAD (CNBV RIPS F36).
 * Guarda TRES campos:
 *   - localidad_clave: clave oficial (8 dígitos) — la que va al reporte SITI.
 *   - localidad: descripción legible — la que ve el operador en pantalla.
 *   - localidad_entidad_snapshot: entidad_federativa vigente al momento del match,
 *     para que la auditoría pueda detectar cambios posteriores del estado sin
 *     retro-actualizar la localidad.
 *
 * Filtro suave por entidad federativa (jul-2026):
 *   - Toggle "Restringir a mi estado" activo por defecto cuando hay estado.
 *   - Si el usuario no encuentra su localidad, desactiva el toggle y busca en
 *     TODO el catálogo. Nunca se queda sin resultados por culpa del filtro.
 *   - El backend además devuelve `filtro_estado_aplicado: false` si el estado
 *     no está en su mapa (extranjero, typo), y se lo informamos al operador.
 */
import { useState } from "react";
import { Search, RefreshCw, CheckCircle2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";

export function LocalidadBuscador({ claveActual, descripcionActual, entidadFederativa, onSelect }) {
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState(true);
  const [filtroAplicadoBackend, setFiltroAplicadoBackend] = useState(null);
  const [totalUltimaBusqueda, setTotalUltimaBusqueda] = useState(null);

  const hayEstado = !!(entidadFederativa && entidadFederativa.trim());

  const buscar = async () => {
    if (!q || q.trim().length < 2) return;
    setBuscando(true);
    try {
      const params = { catalogo: "LOCALIDAD", q };
      if (hayEstado && filtroEstado) {
        params.entidad_federativa = entidadFederativa;
      }
      const { data } = await api.get("/catalogos-rips/buscar", { params });
      setResultados(data.resultados || []);
      setFiltroAplicadoBackend(!!data.filtro_estado_aplicado);
      setTotalUltimaBusqueda(data.total ?? (data.resultados?.length ?? 0));
    } finally {
      setBuscando(false);
    }
  };

  return (
    <div className="mt-2 rounded-md border border-slate-200 bg-slate-50/60 p-3"
         data-testid="localidad-buscador">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-[11px] font-medium text-slate-600">
          Localidad oficial CNBV (opcional, para reportes al SITI)
        </div>
        {hayEstado && (
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer"
                 data-testid="localidad-toggle-estado-label">
            <Switch checked={filtroEstado}
                    onCheckedChange={setFiltroEstado}
                    data-testid="localidad-toggle-estado" />
            <span>Solo {entidadFederativa}</span>
          </label>
        )}
      </div>

      {claveActual ? (
        <div className="mb-2 flex items-start justify-between gap-2 rounded bg-emerald-50 p-2 text-xs text-emerald-800"
             data-testid="localidad-clave-actual">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <div>
              <span className="font-mono font-semibold">{claveActual}</span>
              <span className="ml-2">{descripcionActual}</span>
            </div>
          </div>
          <button type="button" onClick={() => onSelect("", "")}
                  data-testid="localidad-clear-btn"
                  className="text-emerald-800 hover:text-emerald-900">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : null}

      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), buscar())}
               placeholder="Escribe una localidad (ej. Coyoacán, Monterrey, Mérida…)"
               data-testid="localidad-buscador-input"
               className="flex-1 text-sm" />
        <Button type="button" onClick={buscar} disabled={buscando} size="sm" variant="outline"
                data-testid="localidad-buscador-btn">
          {buscando ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
        </Button>
      </div>

      {/* Aviso cuando el filtro por estado NO se pudo aplicar (estado desconocido en el mapa). */}
      {hayEstado && filtroEstado && filtroAplicadoBackend === false && totalUltimaBusqueda !== null && (
        <div className="mt-2 text-[11px] text-amber-700" data-testid="localidad-filtro-no-aplicado">
          El filtro por “{entidadFederativa}” no pudo aplicarse (estado extranjero o fuera del mapa CNBV).
          Se buscó en todo el catálogo.
        </div>
      )}
      {/* Aviso cuando SÍ se aplicó filtro pero no hay resultados: sugerir desactivar. */}
      {hayEstado && filtroEstado && filtroAplicadoBackend === true && totalUltimaBusqueda === 0 && (
        <div className="mt-2 text-[11px] text-slate-600" data-testid="localidad-sin-resultados-estado">
          Sin resultados en {entidadFederativa}. Desactiva “Solo {entidadFederativa}” arriba para
          buscar en todo el catálogo nacional.
        </div>
      )}
      {/* Sin resultados en búsqueda sin filtro: sugerir cambiar el término. */}
      {totalUltimaBusqueda === 0 && !(hayEstado && filtroEstado && filtroAplicadoBackend === true) && (
        <div className="mt-2 text-[11px] text-slate-500" data-testid="localidad-sin-resultados-global">
          Sin resultados. Prueba con otro término (ej. municipio, cabecera, sin acentos).
        </div>
      )}
      {/* Sugerencia UX cuando no hay entidad seleccionada: para maximizar precisión conviene
          elegir primero la entidad federativa; la clave localidad se ata a un estado. */}
      {!hayEstado && (
        <div className="mt-2 text-[11px] text-slate-500" data-testid="localidad-hint-entidad">
          Consejo: selecciona primero la <span className="font-medium">Entidad federativa</span> para
          restringir automáticamente el catálogo y evitar elegir una clave de otro estado.
        </div>
      )}

      {resultados.length > 0 && (
        <div className="mt-2 max-h-52 overflow-y-auto rounded border border-slate-200 bg-white"
             data-testid="localidad-resultados">
          {resultados.map((r) => (
            <button key={r.clave} type="button"
                    data-testid={`localidad-result-${r.clave}`}
                    onClick={() => { onSelect(r.clave, r.descripcion); setResultados([]); setQ(""); }}
                    className="block w-full border-b border-slate-100 px-3 py-2 text-left text-xs hover:bg-emerald-50">
              <span className="font-mono font-semibold text-slate-800">{r.clave}</span>
              <span className="ml-2 text-slate-600">{r.descripcion}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
